import { icon, IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { APP_THEMES } from './appTheme'
import { getAppType } from './appType'
import { BRANDS } from './brand'

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function haptic(pattern: number | number[] = 8) {
  try {
    navigator.vibrate?.(pattern)
  } catch {
    // Some browsers throw when vibrate is called without user activation.
  }
}

const random = (min: number, max: number) => Math.random() * (max - min) + min
const pick = <T>(items: T[]) => items[Math.floor(Math.random() * items.length)]
const themeIcons = () => APP_THEMES[getAppType()].icons
const particleColors = () => [
  APP_THEMES[getAppType()].primary,
  '#ffb800',
  '#22c55e',
  '#24171a',
]

function iconElement(definition: IconDefinition, size: number, color: string) {
  const el = document.createElement('span')
  el.innerHTML = icon(definition).html.join('')
  Object.assign(el.style, {
    display: 'grid',
    placeItems: 'center',
    fontSize: `${size}px`,
    lineHeight: '1',
    color,
  })
  return el
}

function layer() {
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  Object.assign(el.style, {
    position: 'fixed',
    inset: '0',
    pointerEvents: 'none',
    zIndex: '100000',
    overflow: 'hidden',
  })
  document.body.appendChild(el)
  return el
}

export function bump(el: Element | null) {
  if (!el || prefersReducedMotion()) return
  el.animate(
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.25) rotate(-6deg)' },
      { transform: 'scale(0.92) rotate(3deg)' },
      { transform: 'scale(1)' },
    ],
    { duration: 520, easing: 'cubic-bezier(.34,1.56,.64,1)' },
  )
}

function visibleCartTarget(): HTMLElement | null {
  const targets = Array.from(
    document.querySelectorAll<HTMLElement>('[data-cart-target]'),
  )
  return (
    targets.find((el) => {
      const rect = el.getBoundingClientRect()
      return (
        rect.width > 0 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        getComputedStyle(el).visibility !== 'hidden'
      )
    }) ?? null
  )
}

export function flyToCart(source: Element | null, imageSrc?: string) {
  const target = visibleCartTarget()
  if (!source || !target) return
  if (prefersReducedMotion()) {
    bump(target)
    return
  }

  const from = source.getBoundingClientRect()
  const to = target.getBoundingClientRect()
  const size = Math.min(from.width, from.height, 120)
  const root = layer()
  const ghost = document.createElement('img')
  ghost.src = imageSrc || BRANDS[getAppType()].mascot

  Object.assign(ghost.style, {
    position: 'absolute',
    left: `${from.left + from.width / 2 - size / 2}px`,
    top: `${from.top + from.height / 2 - size / 2}px`,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    objectFit: 'cover',
    fontSize: `${size * 0.7}px`,
    display: 'grid',
    placeItems: 'center',
    boxShadow: '0 12px 30px -8px rgba(0,0,0,.35)',
    border: '3px solid #fff',
    background: '#fff',
  })
  root.appendChild(ghost)

  const dx = to.left + to.width / 2 - (from.left + from.width / 2)
  const dy = to.top + to.height / 2 - (from.top + from.height / 2)
  const lift = Math.min(160, Math.abs(dy) * 0.5 + 60)

  const animation = ghost.animate(
    [
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      {
        transform: `translate(${dx * 0.45}px, ${dy * 0.25 - lift}px) scale(.7) rotate(-20deg)`,
        opacity: 1,
        offset: 0.45,
      },
      {
        transform: `translate(${dx}px, ${dy}px) scale(.15) rotate(30deg)`,
        opacity: 0.6,
      },
    ],
    { duration: 720, easing: 'cubic-bezier(.5,0,.4,1)' },
  )

  animation.onfinish = () => {
    root.remove()
    bump(target)
  }
}

export function iconBurst(
  x: number,
  y: number,
  icons: IconDefinition[] = themeIcons(),
  count = 8,
) {
  if (prefersReducedMotion()) return
  const root = layer()
  const colors = particleColors()

  for (let i = 0; i < count; i++) {
    const particle = iconElement(pick(icons), random(14, 22), pick(colors))
    Object.assign(particle.style, {
      position: 'absolute',
      left: `${x}px`,
      top: `${y}px`,
      willChange: 'transform, opacity',
    })
    root.appendChild(particle)

    const angle = (Math.PI * 2 * i) / count + random(-0.3, 0.3)
    const distance = random(40, 90)
    particle.animate(
      [
        { transform: 'translate(-50%,-50%) scale(.4)', opacity: 1 },
        {
          transform: `translate(calc(-50% + ${Math.cos(angle) * distance}px), calc(-50% + ${Math.sin(angle) * distance - 30}px)) scale(1) rotate(${random(-60, 60)}deg)`,
          opacity: 0,
        },
      ],
      { duration: random(600, 900), easing: 'cubic-bezier(.2,.8,.3,1)' },
    )
  }

  window.setTimeout(() => root.remove(), 1000)
}

export function floatText(x: number, y: number, text: string) {
  if (prefersReducedMotion()) return
  const root = layer()
  const el = document.createElement('span')
  el.textContent = text
  Object.assign(el.style, {
    position: 'absolute',
    left: `${x}px`,
    top: `${y}px`,
    font: '900 15px Nunito, sans-serif',
    color: 'var(--color-primary)',
    textShadow: '0 1px 0 #fff, 0 0 8px #fff',
    whiteSpace: 'nowrap',
  })
  root.appendChild(el)
  el.animate(
    [
      { transform: 'translate(-50%, 0) scale(.6)', opacity: 0 },
      { transform: 'translate(-50%, -18px) scale(1.1)', opacity: 1, offset: 0.25 },
      { transform: 'translate(-50%, -54px) scale(1)', opacity: 0 },
    ],
    { duration: 900, easing: 'ease-out' },
  ).onfinish = () => root.remove()
}

export function sushiRain(amount = 42) {
  if (prefersReducedMotion()) return
  const root = layer()
  const icons = themeIcons()
  const colors = particleColors()
  const mascot = BRANDS[getAppType()].mascot
  let longest = 0

  for (let i = 0; i < amount; i++) {
    const size = random(22, 40)
    let drop: HTMLElement
    if (i % 6 === 0) {
      drop = document.createElement('img')
      ;(drop as HTMLImageElement).src = mascot
      Object.assign(drop.style, { width: `${size * 1.4}px`, height: `${size * 1.4}px` })
    } else {
      drop = iconElement(pick(icons), size, pick(colors))
    }
    const duration = random(1800, 3400)
    const delay = random(0, 1400)
    longest = Math.max(longest, duration + delay)
    Object.assign(drop.style, {
      position: 'absolute',
      top: '-60px',
      left: `${random(0, 96)}vw`,
      willChange: 'transform',
    })
    root.appendChild(drop)
    drop.animate(
      [
        { transform: `translateY(0) rotate(${random(-90, 90)}deg)` },
        {
          transform: `translate(${random(-60, 60)}px, ${window.innerHeight + 120}px) rotate(${random(-360, 360)}deg)`,
        },
      ],
      {
        duration,
        delay,
        easing: 'cubic-bezier(.45,.05,.55,.95)',
        fill: 'both',
      },
    )
  }

  window.setTimeout(() => root.remove(), longest + 100)
}

export function confetti(duration = 2600) {
  if (prefersReducedMotion()) return
  const root = layer()
  const canvas = document.createElement('canvas')
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = window.innerWidth * dpr
  canvas.height = window.innerHeight * dpr
  Object.assign(canvas.style, { width: '100%', height: '100%' })
  root.appendChild(canvas)
  const ctx = canvas.getContext('2d')
  if (!ctx) return root.remove()
  ctx.scale(dpr, dpr)

  const primary =
    getComputedStyle(document.documentElement)
      .getPropertyValue('--color-primary')
      .trim() || '#ff1647'
  const colors = [primary, '#ffb800', '#22c55e', '#3b82f6', '#ff7ab6', '#ffffff']
  const pieces = Array.from({ length: 160 }, () => ({
    x: window.innerWidth / 2 + random(-60, 60),
    y: window.innerHeight * 0.55,
    vx: random(-9, 9),
    vy: random(-17, -7),
    w: random(6, 11),
    h: random(8, 16),
    r: random(0, Math.PI),
    vr: random(-0.25, 0.25),
    color: pick(colors),
  }))

  const start = performance.now()
  const frame = (now: number) => {
    const t = now - start
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
    ctx.globalAlpha = Math.max(0, 1 - Math.max(0, t - duration * 0.7) / (duration * 0.3))

    for (const p of pieces) {
      p.vy += 0.32
      p.vx *= 0.99
      p.x += p.vx
      p.y += p.vy
      p.r += p.vr
      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.rotate(p.r)
      ctx.fillStyle = p.color
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.r * 2)))
      ctx.restore()
    }

    if (t < duration) requestAnimationFrame(frame)
    else root.remove()
  }

  requestAnimationFrame(frame)
}
